export type OrgDepthLimit = 'allowed' | 'blocked' | 'unknown';

/**
 * The shape this module reads off `childOrgs.currentOrg`. `parentId` is
 * optional on purpose: a response that never carried the field must not be
 * read as "this org has no parent".
 */
type CurrentOrg = {
  id?: string | null;
  parentId?: string | null;
};

/**
 * Whether a new org may be created UNDER `selectedOrgId` (PSTUDIO-4100).
 *
 * The hierarchy is exactly two levels — a parent org plus one level of child
 * orgs (PSTUDIO-4097 enforces it on the backend). An org that already has a
 * parent is a child, so nothing may be created beneath it.
 *
 * Three answers, because the missing case is not a "yes":
 * - `blocked` — the org is known to have a parent.
 * - `allowed` — the org is known to have none (`null` or an empty string).
 * - `unknown` — `currentOrg` is absent, describes a different org than the
 *   selected one (the context keeps the last load until the next one lands),
 *   or never carried `parentId`.
 *
 * LIMITATION: `unknown` is NOT treated as blocked by the callers, so for that
 * window the UI offers the action and the backend rule is the only guard. That
 * is deliberate — blocking on missing data would disable the control for a
 * top-level org whenever the load is slow or has failed.
 */
export const resolveOrgDepthLimit = ({
  selectedOrgId,
  currentOrg,
}: {
  selectedOrgId: string | null | undefined;
  currentOrg: CurrentOrg | null | undefined;
}): OrgDepthLimit => {
  if (!selectedOrgId || !currentOrg) return 'unknown';
  if (currentOrg.id !== selectedOrgId) return 'unknown';
  if (currentOrg.parentId === undefined) return 'unknown';

  return currentOrg.parentId ? 'blocked' : 'allowed';
};

/**
 * Whether the create page should ask the machine to load the selected org's
 * data (PSTUDIO-4100).
 *
 * `requestedFor` is what makes this safe to call from an effect: a load can
 * finish and still leave the depth `unknown` — it failed, or `currentOrg` was
 * overwritten for another org in the meantime — and the machine's state then
 * changes again. Asking on every such change would loop; asking once per org
 * does not. The cost is that a failed load is not retried from this page.
 */
export const shouldRequestOrgLoad = ({
  selectedOrgId,
  depthLimit,
  canRequest,
  requestedFor,
}: {
  selectedOrgId: string | null | undefined;
  depthLimit: OrgDepthLimit;
  canRequest: boolean;
  requestedFor: string | null | undefined;
}): boolean =>
  !!selectedOrgId &&
  depthLimit === 'unknown' &&
  canRequest &&
  requestedFor !== selectedOrgId;
